import { useContext, useEffect, useState } from 'react';
import { Box, Button, Flex, Heading, Text, VStack, HStack } from 'src/components/ui';
import { useNavigate } from 'react-router-dom';

import {
	DataTable,
	DataTablePagination,
} from 'src/components/common/DataTable';
import FusionLoading from 'src/components/common/FusionLoading';
import UserContext from 'src/contexts/user/UserContext';
import usePagination from 'src/hooks/usePagination';
import { getCampaignsListingColumns } from 'src/components/campaign/parts/campaignListingColumns';
import {
	getCampaigns,
	archiveCampaign,
	duplicateCampaign,
	deactivateCampaign,
} from 'src/services/campaign';
import { ICampaignsListingItem } from 'src/lib/schemas';
import { toastError } from 'src/services/toast';
import useGroups from 'src/hooks/useGroups';
import CustomContainer from 'src/components/common/CustomContainer';
import useWindowSize from 'src/hooks/useWindowSize';
import { BREAKPOINTS } from 'src/components/campaigns/utils/constants';
import Chip from 'src/components/common/Chip';
import CampaignOptionsMenu from 'src/components/campaign/parts/CampaignOptionsMenu';

interface CampaignsProps {
	withContainer?: boolean;
	simplified?: boolean;
	noCampaignMessage?: () => JSX.Element;
	onCampaignsLoaded?: (campaigns: ICampaignsListingItem[]) => void;
}
const Campaigns = ({
	withContainer = true,
	simplified = false,
	noCampaignMessage,
	onCampaignsLoaded,
}: CampaignsProps) => {
	const [campaigns, setCampaigns] = useState<Array<ICampaignsListingItem>>();
	const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
	const { data: groups, isLoading: isGroupsLoading } = useGroups();
	const { account, isLoading: isAccountLoading } = useContext(UserContext);
	const { width } = useWindowSize();
	const isMobile = width < BREAKPOINTS.mobile;

	const pagination = usePagination();
	const navigate = useNavigate();

	const handleCreateCampaign = () => {
		navigate('/projects/campaigns/new');
	};

	const fetchCampaigns = async () => {
		setIsLoadingCampaigns(true);
		try {
			const query = `page=${pagination.page}&status[]=published&status[]=draft&status[]=finalized&status[]=publishing`;

			const { results, totalPages } = await getCampaigns(query);
			const filteredResults = results.filter(
				(result) => result.status !== 'archived',
			);
			let mappedResults = filteredResults.map((result) => ({
				id: result.id,
				title: result.title,
				status: result.status,
				progress: 0,
				group: result.group,
			}));

			if (simplified) {
				mappedResults = mappedResults.slice(0, 5);
			}

			setCampaigns(mappedResults);
			pagination.setPages(totalPages);
		} catch (error: any) {
			toastError(error);
		}
		setIsLoadingCampaigns(false);
	};

	useEffect(() => {
		fetchCampaigns();
	}, [pagination.page, simplified]);

	useEffect(() => {
		campaigns && onCampaignsLoaded && onCampaignsLoaded(campaigns);
	}, [campaigns]);

	const handleViewCampaign = (id: string) => {
		navigate(`/projects/campaigns/${id}`);
	};

	const handleDeleteCampaign = async (id: string): Promise<void> => {
		try {
			await archiveCampaign(id);

			setCampaigns((prevCampaigns) =>
				prevCampaigns?.filter((campaign) => campaign.id !== id),
			);
		} catch (error: any) {
			toastError(error);
		}
	};
	const handleDuplicateCampaign = async (id: string): Promise<void> => {
		try {
			const duplicatedCampaign = await duplicateCampaign(id);
			const newCampaign: ICampaignsListingItem = {
				id: duplicatedCampaign.id,
				title: duplicatedCampaign.title,
				status: duplicatedCampaign.status,
				progress: 0,
				group: duplicatedCampaign.group,
			};

			setCampaigns((prevCampaigns) => [...(prevCampaigns ?? []), newCampaign]);

			navigate(`/projects/campaigns/${duplicatedCampaign.id}`);
		} catch (error) {
			toastError('Failed to duplicate campaign.');
		}
	};

	const handleDeactivateCampaign = async (id: string): Promise<void> => {
		try {
			await deactivateCampaign(id);
			setCampaigns((prevCampaigns) =>
				prevCampaigns?.map((campaign) =>
					campaign.id === id ? { ...campaign, status: 'draft' } : campaign,
				),
			);
		} catch (error) {
			toastError('Failed to deactivate campaign.');
		}
	};

	const columns = getCampaignsListingColumns({
		onViewCampaign: handleViewCampaign,
		onDeleteCampaign: handleDeleteCampaign,
		onDuplicateCampaign: handleDuplicateCampaign,
		onDeactivateCampaign: handleDeactivateCampaign,
		groups: groups ?? [],
		simplified,
	});

	const showCampaigns = !isAccountLoading;

	const renderMobileCards = () => {
		if (isLoadingCampaigns) {
			return <FusionLoading isLoading={true} />;
		}

		if (!campaigns?.length) {
			return noCampaignMessage ? <Box>{noCampaignMessage()}</Box> : null;
		}

		return (
			<VStack spacing={3} align="stretch">
				{campaigns.map((campaign) => {
					const groupId = campaign.group;
					const group = groups?.find((g) => g.id === groupId);
					const title = campaign.title?.trim();

					return (
						<Box
							key={campaign.id}
							bg="white"
							border="1px solid #F5F5F5"
							borderRadius="md"
							p={4}
							onClick={() => handleViewCampaign(campaign.id)}
							cursor="pointer"
							_hover={{ bg: 'gray.50' }}
						>
							<Flex justify="space-between" align="flex-start">
								<VStack align="flex-start" spacing={2} flex={1}>
									<Text
										fontSize="16px"
										fontWeight="600"
										color="#202223"
										fontStyle={!title ? 'italic' : 'normal'}
									>
										{!title ? 'No title' : title}
									</Text>
									{!simplified && group && (
										<Text fontSize="14px" color="gray.600">
											{group.name}
										</Text>
									)}
								{!simplified && (
									<Chip
										label={
											campaign.status === 'draft' ? 'Draft' :
											campaign.status === 'finalized' ? 'Finalized' :
											campaign.status === 'archived' ? 'Archived' : 'Published'
										}
										tagProps={{
											colorScheme:
												campaign.status === 'draft' ? 'gray' :
												campaign.status === 'finalized' ? 'blue' :
												campaign.status === 'archived' ? 'red' : 'green',
											variant: 'outline',
											size: 'sm',
										}}
									/>
								)}
								</VStack>
								<Box onClick={(e) => e.stopPropagation()}>
									<CampaignOptionsMenu
										onEdit={() => handleViewCampaign(campaign.id)}
										onDuplicate={() => handleDuplicateCampaign(campaign.id)}
										onDelete={() => handleDeleteCampaign(campaign.id)}
										onDeactivate={() => handleDeactivateCampaign(campaign.id)}
										status={campaign.status as 'draft' | 'published' | 'archived' | 'finalized' | 'publishing'}
										campaignTitle={campaign.title}
									/>
								</Box>
							</Flex>
						</Box>
					);
				})}
			</VStack>
		);
	};

	const renderPage = () => (
		<Box>
			<FusionLoading isLoading={isAccountLoading} />
			{showCampaigns && (
				<Flex direction="column" gap={6}>
					<Flex alignItems="center" justifyContent="space-between">
						<Heading fontSize={{ base: '20px', md: '24px' }}>
							{simplified ? 'Recent campaigns' : 'Campaigns'}
						</Heading>
						<Button
							size="sm"
							onClick={handleCreateCampaign}
							variant="orangeSolid"
						>
							New
						</Button>
					</Flex>
					{isMobile ? (
						<>
							{renderMobileCards()}
							{!simplified && campaigns && campaigns.length > 0 && (
								<DataTablePagination {...pagination} />
							)}
						</>
					) : (
						<>
							{!campaigns?.length && noCampaignMessage ? (
								<Box>{noCampaignMessage()}</Box>
							) : (
								campaigns && (
									<>
										<DataTable
											columns={columns}
											data={campaigns!}
											isLoading={isLoadingCampaigns || isGroupsLoading}
											tableProps={{
												border: '1px solid #F5F5F5',
												minH: '400px',
											}}
											trProps={{
												borderBottomWidth: '1px',
												borderColor: '#F5F5F5',
											}}
										/>
										{!simplified && <DataTablePagination {...pagination} />}
									</>
								)
							)}
						</>
					)}
				</Flex>
			)}
		</Box>
	);

	return (
		<Box w="-webkit-fill-available">
			{withContainer ? (
				<CustomContainer>{renderPage()}</CustomContainer>
			) : (
				renderPage()
			)}
		</Box>
	);
};

export default Campaigns;
